import styled from 'styled-components';

import { useAccountView } from '#/features/account/account-context';
import { DepositModalProvider } from '#/features/deposit/modal-context';
import { WithdrawModalProvider } from '#/features/withdraw/modal-context';
import { usePortfolioSummaryView } from '#/features/account/portfolio-summary-context';
import { useAccountSummaryView } from '#/features/account/account-summary-context';
import { useMarginSummaryView } from '#/features/account/margin-summary-context';

import DepositModal from '#/components/common/deposit/DepositModal';
import WithdrawModal from '#/components/withdraw/WithdrawModal';
import Tooltip, { HelpUnderlineHover } from '#/components/shared/Tooltip';
import ValueChange from '#/components/shared/ValueChange';
import { MEDIA } from '#/components/shared/media-queries';
import { HorizontalSpacer } from '#/components/shared/Spacer';
import Description from '#/components/perpetuals/Description';
import DescriptionList, {
  Container as DescriptionListContainer,
} from '#/components/perpetuals/DescriptionList';

import ContractAddress from './ContractAddress';
import { Card, CardSecondary, CardTitle } from './atoms';
import DepositButton from './DepositButton';
import WithdrawButton from './WithdrawButton';
import PortfolioTradeOverview from './PortfolioTradeOverview';

export default function PortfolioOverview() {
  return (
    <DepositModalProvider>
      <WithdrawModalProvider>
        <PortfolioOverviewContent />
      </WithdrawModalProvider>
    </DepositModalProvider>
  );
}

function PortfolioOverviewContent() {
  const accountSummaryView = useAccountSummaryView();
  const marginSummaryView = useMarginSummaryView();

  return (
    <Container>
      <DepositModal />
      <WithdrawModal />
      <Header data-testid="portfolio-overview-page-header">
        <Heading>Wallet</Heading>
        <TransferActions>
          <DepositButton />
          <WithdrawButton />
        </TransferActions>
      </Header>
      <Body>
        <PrimaryGrid>
          <AccountValue />
          <FreeCollateral />
          <HealthFactor />
          <UnrealizedPnl />
        </PrimaryGrid>
        <SecondaryGrid>
          <CardSecondary>
            <DescriptionList title="Account" expanded>
              <Description
                label="Collateral Value"
                value={accountSummaryView.totalCollateral}
              />
              <Description
                label="Position Notional"
                value={accountSummaryView.positionNotional}
              />
              <Description
                label="Account Leverage"
                value={accountSummaryView.leverage}
              />
            </DescriptionList>
          </CardSecondary>
          <CardSecondary>
            <DescriptionList title="Margin" expanded>
              <Description
                label="Initial Margin"
                value={marginSummaryView.initialMarginRequirement}
              />
              <Description
                label="Maintenance Margin"
                value={marginSummaryView.maintenanceMarginRequirement}
              />
              <Description
                label="Health Factor"
                value={marginSummaryView.healthFactor}
              />
            </DescriptionList>
          </CardSecondary>
          {/* Temporarily disabled till API implemented */}
          <div css="display: none">
            <CardSecondary>
              <DescriptionList
                title="Greeks"
                ContainerComponent={GreeksContainer}
              >
                <Description label="Delta" value="-" />
                <Description label="Gamma" value="-" />
                <Description label="Vega" value="-" />
                <Description label="Theta" value="-" />
                <Description label="Rho" value="-" />
              </DescriptionList>
            </CardSecondary>
          </div>
        </SecondaryGrid>

        {/* Temporarily hide for trading competition */}
        <div css="display: none">
          <ContractAddress />
        </div>

        <HorizontalSpacer css="margin-top: 16px" />

        <ContainerTradeOverview>
          <PortfolioTradeOverview />
        </ContainerTradeOverview>
      </Body>
    </Container>
  );
}

function AccountValue() {
  const view = usePortfolioSummaryView();

  return (
    <Card>
      <Tooltip label="The total value of your account at current mark prices">
        <HelpUnderlineHover as={CardTitle}>Account Value</HelpUnderlineHover>
      </Tooltip>
      <Value>{view.accountValue}</Value>
    </Card>
  );
}

function HealthFactor() {
  const accountView = useAccountView();

  return (
    <Card>
      <Tooltip label="Account value / maintenance margin requirements">
        <HelpUnderlineHover as={CardTitle}>Health Factor</HelpUnderlineHover>
      </Tooltip>
      <Value>{accountView.healthFactor}</Value>
    </Card>
  );
}

function FreeCollateral() {
  const accountView = useAccountView();

  return (
    <Card>
      <CardTitle>Free Collateral</CardTitle>
      <Value>{accountView.freeCollateral}</Value>
    </Card>
  );
}

function UnrealizedPnl() {
  const accountSummaryView = useAccountSummaryView();
  return (
    <Card>
      <CardTitle>Unrealized P&L</CardTitle>
      <ValueChange
        as={Value}
        direction={accountSummaryView.unrealizedPnlDirection}
      >
        {accountSummaryView.unrealizedPnl}
      </ValueChange>
    </Card>
  );
}

const Container = styled.div`
  margin: 0 auto;
  max-width: 1100px;
  width: 100%;
  min-height: 100%;

  display: grid;
  grid-template-rows:
    auto
    1fr;
`;

const ContainerTradeOverview = styled.div`
  overflow: auto;

  @media ${MEDIA.md} {
    overflow: unset;
  }
`;

const Header = styled.div`
  display: grid;
  grid-auto-flow: row;
  gap: 24px;
  margin-top: 44px;
  margin-bottom: 24px;

  @media ${MEDIA.md} {
    display: flex;
    justify-content: space-between;
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-rows:
    auto
    auto
    auto
    1fr;

  gap: 24px;
`;

const PrimaryGrid = styled.div`
  display: grid;
  gap: 12px 16px;
  grid-auto-flow: row;

  @media ${MEDIA.md} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const SecondaryGrid = styled.div`
  display: grid;
  grid-auto-flow: row;
  gap: 12px 16px;

  @media ${MEDIA.md} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const GreeksContainer = styled(DescriptionListContainer)`
  grid-auto-flow: column;
  grid-template-rows: repeat(3, 1fr);
  gap: 12px 16px;
`;

const Heading = styled.h1`
  font-size: 24px;
  line-height: 24px;
  margin: 0;
  font-weight: 700;
  align-self: flex-end;
`;

const Value = styled.span`
  font-size: 21px;
  line-height: 21px;
`;

const TransferActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(125px, 1fr));
  gap: 12px;
`;
